import { Box, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import DarkMode from "@mui/icons-material/DarkMode";
import LightMode from "@mui/icons-material/LightMode";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import HelpOutline from "@mui/icons-material/HelpOutline";
import MenuRounded from "@mui/icons-material/MenuRounded";
import MenuOpenRounded from "@mui/icons-material/MenuOpenRounded";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { TouchTooltip } from "@/components/TouchTooltip";
import { CreateButton } from "./CreateButton";
import { DESKTOP_ITEMS } from "./nav-items";
import { useThemeStore } from "@/store/theme-store";
import { useUIStore } from "@/store/ui-store";
import { useSignOutAny } from "@/lib/auth";
import { TopAppBar } from "./TopAppBar";
import { IdleSentinel } from "@/components/IdleSentinel";
import { VersionBadge } from "@/components/VersionBadge";

const RAIL_OPEN   = 260;
const RAIL_CLOSED = 76;

/** Height every row (nav items + footer buttons) uses, so nothing shifts
 *  vertically when the rail collapses. Icon stays pinned to the same Y. */
const ROW_H = 48;

export function DesktopShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const mode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const signOutAny = useSignOutAny();
  const collapsed = useUIStore((s) => s.navCollapsed);
  const toggleNav = useUIStore((s) => s.toggleNav);

  const width = collapsed ? RAIL_CLOSED : RAIL_OPEN;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Engagement tracker + idle-confirm modal. Sits at shell root
       *  so one instance covers every page. Invisible at rest. */}
      <IdleSentinel />
      <Drawer
        variant="permanent"
        sx={{
          width,
          flexShrink: 0,
          transition: "width .22s ease",
          [`& .MuiDrawer-paper`]: {
            width,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: "width .22s ease",
            display: "flex",
            flexDirection: "column",
            px: collapsed ? 1 : 1.5,
            py: 2
          }
        }}
      >
        {/* Header row: "Menu" label + collapse toggle, fixed height.
         *  WHB logo + wordmark now live in the TopAppBar, not the sidebar. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={collapsed ? "center" : "space-between"}
          sx={{ height: ROW_H, mb: 2, minHeight: ROW_H, px: collapsed ? 0 : 0.5 }}
        >
          {!collapsed && (
            <Typography
              sx={{
                fontFamily: '"Roboto Flex Variable", sans-serif',
                fontWeight: 700,
                fontSize: "0.8125rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "text.secondary",
                pl: 0.5
              }}
            >
              Menu
            </Typography>
          )}
          <Tooltip title={collapsed ? "Expand menu" : "Collapse menu"} placement="right">
            <IconButton
              onClick={toggleNav}
              aria-label={collapsed ? "Expand menu" : "Collapse menu"}
              size="small"
            >
              {collapsed ? <MenuRounded /> : <MenuOpenRounded />}
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Create button (Google-Ads style) with dropdown of actions */}
        <CreateButton collapsed={collapsed} />

        {/* Nav list — horizontal [icon] [label] rows, fixed-height */}
        <List disablePadding sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {DESKTOP_ITEMS.map((item) => {
            const Icon = item.icon;
            const active =
              item.path === "/accounts"
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);

            return (
              <ListItem disablePadding key={item.path} sx={{ height: ROW_H }}>
                <TouchTooltip
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.description}
                      </Typography>
                    </Box>
                  }
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    component={NavLink}
                    to={item.path}
                    selected={active}
                    sx={{
                      height: ROW_H,
                      minHeight: ROW_H,
                      px: collapsed ? 0 : 1.25,
                      justifyContent: collapsed ? "center" : "flex-start",
                      borderRadius: 2
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed ? "auto" : 36,
                        color: "inherit",
                        justifyContent: "center"
                      }}
                    >
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    {!collapsed && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontWeight: active ? 700 : 600,
                          fontSize: "0.9375rem"
                        }}
                      />
                    )}
                  </ListItemButton>
                </TouchTooltip>
              </ListItem>
            );
          })}
        </List>

        {/* Footer rail — fixed-height rows, same horizontal layout */}
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <FooterRow
            icon={mode === "dark" ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
            label={mode === "dark" ? "Light mode" : "Dark mode"}
            tooltip={`Switch to ${mode === "dark" ? "light" : "dark"} mode`}
            collapsed={collapsed}
            onClick={toggleTheme}
          />
          <FooterRow
            icon={<HelpOutline fontSize="small" />}
            label="Help & FAQ"
            tooltip="Help & FAQ"
            collapsed={collapsed}
            onClick={() => navigate("/help")}
          />
          <FooterRow
            icon={<LogoutRounded fontSize="small" />}
            label="Sign out"
            tooltip="Sign out"
            collapsed={collapsed}
            onClick={() => { signOutAny(); }}
            color={theme.palette.error.main}
          />
          {/* Build marker — small, always visible, taps through to the
           *  release history. Hidden when the rail is collapsed to an
           *  icon strip since there's no room for the label. */}
          {!collapsed && (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 0.5 }}>
              <VersionBadge />
            </Box>
          )}
        </Stack>
      </Drawer>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <TopAppBar />
        <Box
          component="main"
          sx={{
            flex: 1,
            px: { md: 4, lg: 6 },
            py: 4,
            maxWidth: 1280,
            width: "100%",
            mx: "auto"
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

/* -- Footer row — mirrors a nav item's layout so Y positions align ----------- */

function FooterRow({
  icon, label, tooltip, collapsed, onClick, color
}: {
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  collapsed: boolean;
  onClick: () => void;
  color?: string;
}) {
  return (
    <Tooltip title={tooltip} placement="right">
      <ListItemButton
        onClick={onClick}
        sx={{
          height: ROW_H,
          minHeight: ROW_H,
          px: collapsed ? 0 : 1.25,
          justifyContent: collapsed ? "center" : "flex-start",
          borderRadius: 2,
          color: color ?? "inherit"
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? "auto" : 36,
            color: "inherit",
            justifyContent: "center"
          }}
        >
          {icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }} />
        )}
      </ListItemButton>
    </Tooltip>
  );
}
